import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

import api from '../../../../services/api';

// Onda 3 · aba "NPS": score do período, série de 12 meses, por motorista
// e últimos comentários do cliente final.

interface INpsSummary {
  answered: number;
  sent: number;
  responseRate: number | null;
  nps: number | null;
  promoters: number;
  neutrals: number;
  detractors: number;
  monthly: { month: string; answered: number; nps: number | null }[];
  byDriver: {
    driverName: string;
    answered: number;
    avgRating: number | null;
    nps: number | null;
  }[];
  comments: {
    rating: number;
    reason: string;
    date: string;
    customerName: string;
  }[];
}

const corNps = (nps: number | null) => {
  if (nps === null) return '#5A6B7A';
  if (nps >= 50) return '#2E7D32';
  if (nps >= 0) return '#9A6A0B';
  return '#C43D3D';
};

const fmtPct = (v: number | null) =>
  v === null ? '—' : `${Math.round(v * 100)}%`;

export const NpsTab = ({ period }: { period: string }) => {
  const [data, setData] = useState<INpsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/summarizers/nps-summary', {
        params: { period },
      });
      setData(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" p={8}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data) return null;

  const maxAnswered = Math.max(...data.monthly.map((m) => m.answered), 1);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3} md={2}>
            <Paper style={{ padding: 14, textAlign: 'center' }}>
              <Typography
                variant="h4"
                style={{ fontWeight: 800, color: corNps(data.nps) }}
              >
                {data.nps === null ? '—' : data.nps}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                NPS do período
              </Typography>
            </Paper>
          </Grid>
          {[
            ['Respostas', String(data.answered)],
            ['Taxa de resposta', fmtPct(data.responseRate)],
            ['Promotores (9-10)', String(data.promoters)],
            ['Neutros (7-8)', String(data.neutrals)],
            ['Detratores (0-6)', String(data.detractors)],
          ].map(([label, valor]) => (
            <Grid item xs={6} sm={3} md={2} key={label}>
              <Paper style={{ padding: 14, textAlign: 'center' }}>
                <Typography variant="h5" style={{ fontWeight: 700 }}>
                  {valor}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid item xs={12} md={5}>
        <Paper style={{ padding: 16 }}>
          <Typography variant="subtitle2" gutterBottom>
            Últimos 12 meses
          </Typography>
          {data.monthly.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Sem respostas no período.
            </Typography>
          ) : (
            data.monthly.map((m) => (
              <Box
                key={m.month}
                display="flex"
                alignItems="center"
                gridGap={8}
                mb={0.5}
              >
                <span style={{ fontSize: 11, width: 56, color: '#5A6B7A' }}>
                  {m.month.slice(5)}/{m.month.slice(2, 4)}
                </span>
                <Box
                  flex={1}
                  height={10}
                  borderRadius={5}
                  style={{ background: '#eef3f4', overflow: 'hidden' }}
                >
                  <Box
                    height={10}
                    style={{
                      width: `${(m.answered / maxAnswered) * 100}%`,
                      background: corNps(m.nps),
                      borderRadius: 5,
                    }}
                  />
                </Box>
                <span
                  style={{
                    fontSize: 11,
                    width: 60,
                    textAlign: 'right',
                    fontWeight: 700,
                    color: corNps(m.nps),
                  }}
                >
                  {m.nps === null ? '—' : `NPS ${m.nps}`}
                </span>
              </Box>
            ))
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={7}>
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Motorista</TableCell>
                <TableCell align="right">Respostas</TableCell>
                <TableCell align="right">Nota média</TableCell>
                <TableCell align="right">NPS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.byDriver.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="textSecondary">
                      Sem respostas ligadas a motoristas no período.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.byDriver.map((d) => (
                  <TableRow key={d.driverName} hover>
                    <TableCell>{d.driverName}</TableCell>
                    <TableCell align="right">{d.answered}</TableCell>
                    <TableCell align="right">
                      {d.avgRating === null ? '—' : d.avgRating.toFixed(1)}
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{ fontWeight: 700, color: corNps(d.nps) }}
                    >
                      {d.nps === null ? '—' : d.nps}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper style={{ padding: 16 }}>
          <Typography variant="subtitle2" gutterBottom>
            Últimos comentários
          </Typography>
          {data.comments.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Nenhum comentário no período.
            </Typography>
          ) : (
            data.comments.map((c, index) => (
              <Box
                key={`${c.date}-${index}`}
                py={1}
                style={{
                  borderBottom:
                    index < data.comments.length - 1
                      ? '1px solid #eef1f2'
                      : undefined,
                }}
              >
                <Typography variant="body2">
                  <b
                    style={{
                      color: c.rating >= 9 ? '#2E7D32' : c.rating <= 6 ? '#C43D3D' : '#9A6A0B',
                    }}
                  >
                    {c.rating}/10
                  </b>{' '}
                  — {c.reason}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {c.customerName}
                  {c.date
                    ? ` · ${new Date(c.date).toLocaleDateString('pt-BR')}`
                    : ''}
                </Typography>
              </Box>
            ))
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};
